import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCookies } from 'react-cookie';
import { useHistory, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { ChamadoService } from 'services/main/endpoints/ChamadoService';
import { FiltersService } from 'services/reports/endpoints/FiltersService';
import { URLS } from 'constants/urls';

import type {
  FieldConfigMap,
  FieldId,
  FormValues,
  OptionsState,
  PageLabels,
  QrCodeCookieData,
  QrCodeType,
  SectionKey,
  UseChamadoSemLoginReturn,
} from '../types';

// ── Constantes ──────────────────────────────────────────────────────────────

const DEFAULT_BRAND_COLOR = '#1D5DA9';
const DEFAULT_PAGE_TITLE = 'Abertura de chamado';
const DEFAULT_SUPPORT_TEXT =
  'Nossa equipe técnica analisará sua solicitação e realizará as ações necessárias o mais breve possível.';
const DEFAULT_SUCCESS_TITLE = 'Sua solicitação foi registrada';
const DEFAULT_SUCCESS_SUPPORT_TEXT = 'Realizaremos as ações necessárias o mais breve possível.';

interface FieldMeta {
  defaultLabel: string;
  labelKey: string;
  exibirKey: string;
  obrigatorioKey: string;
  forceVisible?: boolean;
  forceRequired?: boolean;
}

const FIELD_META: Record<FieldId, FieldMeta> = {
  name: {
    defaultLabel: 'Seu nome',
    labelKey: 'emitenteLabel',
    exibirKey: 'emitenteExibir',
    obrigatorioKey: 'emitenteObrigatorio',
  },
  phone: {
    defaultLabel: 'Telefone',
    labelKey: 'telefoneLabel',
    exibirKey: 'telefoneExibir',
    obrigatorioKey: 'telefoneObrigatorio',
  },
  email: {
    defaultLabel: 'E-mail',
    labelKey: 'emailLabel',
    exibirKey: 'emailExibir',
    obrigatorioKey: 'emailObrigatorio',
  },
  unit: {
    defaultLabel: 'Qual a unidade?',
    labelKey: 'siteLabel',
    exibirKey: 'siteExibir',
    obrigatorioKey: 'siteObrigatorio',
    forceVisible: true,
    forceRequired: true,
  },
  areaGroup: {
    defaultLabel: 'Grupo de ambientes',
    labelKey: 'grupoAreaLabel',
    exibirKey: 'grupoAreaExibir',
    obrigatorioKey: 'grupoAreaObrigatorio',
  },
  areaSubgroup: {
    defaultLabel: 'Subgrupo de ambientes',
    labelKey: 'subgrupoAreaLabel',
    exibirKey: 'subgrupoAreaExibir',
    obrigatorioKey: 'subgrupoAreaObrigatorio',
  },
  area: {
    defaultLabel: 'Ambiente',
    labelKey: 'areaLabel',
    exibirKey: 'areaExibir',
    obrigatorioKey: 'areaObrigatoria',
  },
  equipment: {
    defaultLabel: 'Equipamento',
    labelKey: 'equipamentoLabel',
    exibirKey: 'equipamentoExibir',
    obrigatorioKey: 'equipamentoObrigatorio',
  },
  serviceType: {
    defaultLabel: 'Qual é o tipo de serviço?',
    labelKey: 'sistemaLabel',
    exibirKey: 'sistemaExibir',
    obrigatorioKey: 'sistemaObrigatorio',
  },
  foundSituation: {
    defaultLabel: 'Situação encontrada',
    labelKey: 'anomaliaTipicaLabel',
    exibirKey: 'anomaliaTipicaExibir',
    obrigatorioKey: 'anomaliaTipicaObrigatoria',
  },
  priority: {
    defaultLabel: 'Prioridade',
    labelKey: 'prioridadeLabel',
    exibirKey: 'prioridadeExibir',
    obrigatorioKey: 'prioridadeObrigatoria',
  },
  sector: {
    defaultLabel: 'Setor',
    labelKey: 'setorLabel',
    exibirKey: 'setorExibir',
    obrigatorioKey: 'setorObrigatorio',
  },
  details: {
    defaultLabel: 'Conte mais detalhes',
    labelKey: 'descricaoLabel',
    exibirKey: 'descricaoExibir',
    obrigatorioKey: 'descricaoObrigatoria',
    forceVisible: true,
    forceRequired: true,
  },
  photos: {
    defaultLabel: 'Fotos do problema',
    labelKey: 'fotoLabel',
    exibirKey: 'fotoExibir',
    obrigatorioKey: 'fotoObrigatoria',
  },
};

const DEFAULT_FIELD_CONFIG = (Object.keys(FIELD_META) as FieldId[]).reduce((acc, id) => {
  const meta = FIELD_META[id];
  acc[id] = {
    label: meta.defaultLabel,
    visible: meta.forceVisible ?? false,
    required: meta.forceRequired ?? false,
    locked: false,
  };
  return acc;
}, {} as FieldConfigMap);

type ApiConfig = Record<string, unknown> & { labels?: Record<string, unknown> };

function buildFieldConfig(config: ApiConfig): FieldConfigMap {
  return (Object.keys(FIELD_META) as FieldId[]).reduce((acc, id) => {
    const meta = FIELD_META[id];
    const labelFromApi = config?.labels?.[meta.labelKey];
    const exibir = config?.[meta.exibirKey];
    const obrigatorio = config?.[meta.obrigatorioKey];

    const visible = meta.forceVisible || (typeof exibir === 'boolean' ? exibir : false);
    const required = meta.forceRequired || (visible && (typeof obrigatorio === 'boolean' ? obrigatorio : false));

    acc[id] = {
      label: typeof labelFromApi === 'string' && labelFromApi.trim() ? labelFromApi : meta.defaultLabel,
      visible,
      required,
      locked: false,
    };
    return acc;
  }, {} as FieldConfigMap);
}

function buildPageLabels(config: ApiConfig): PageLabels {
  const labels = config?.labels as Record<string, string> | undefined;
  return {
    logoPath: labels?.logotipoPath?.trim() || undefined,
    brandColor: labels?.corMarca?.trim() || DEFAULT_BRAND_COLOR,
    pageTitle: labels?.tituloPagina?.trim() || DEFAULT_PAGE_TITLE,
    supportText: labels?.textoApoio?.trim() || DEFAULT_SUPPORT_TEXT,
    successTitle: labels?.tituloPaginaSucesso?.trim() || DEFAULT_SUCCESS_TITLE,
    successSupportText: labels?.textoApoioSucesso?.trim() || DEFAULT_SUCCESS_SUPPORT_TEXT,
  };
}

function getQrCodeType(code: string): QrCodeType {
  if (code.startsWith('E')) return 'E';
  if (code.startsWith('A')) return 'A';
  return 'S';
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useChamadoSemLogin(): UseChamadoSemLoginReturn {
  const { qrcode } = useParams<{ qrcode: string }>();
  const { push } = useHistory();
  const [cookie, , removeCookie] = useCookies();

  const chamadoWobaCookie = cookie?.chamadoWoba as QrCodeCookieData | undefined;
  const empresaId = chamadoWobaCookie?.empresa as number | undefined;
  const unitId = chamadoWobaCookie?.site;
  const qrCodeType = useMemo(() => getQrCodeType(qrcode || ''), [qrcode]);

  const [revealedSections, setRevealedSections] = useState<SectionKey[]>(['contact']);
  const [isSuccess, setIsSuccess] = useState(false);
  const [fieldConfig, setFieldConfig] = useState<FieldConfigMap>(DEFAULT_FIELD_CONFIG);
  const [options, setOptions] = useState<OptionsState>({});
  const [pageLabels, setPageLabels] = useState<PageLabels>({
    brandColor: DEFAULT_BRAND_COLOR,
    pageTitle: DEFAULT_PAGE_TITLE,
    supportText: DEFAULT_SUPPORT_TEXT,
    successTitle: DEFAULT_SUCCESS_TITLE,
    successSupportText: DEFAULT_SUCCESS_SUPPORT_TEXT,
  });
  const [notifyRequestersByEmail, setNotifyRequestersByEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const areaPageRef = useRef(1);
  const loadingMoreAreasRef = useRef(false);
  const [areaSearch, setAreaSearch] = useState('');
  const equipamentoPageRef = useRef(1);
  const loadingMoreEquipamentosRef = useRef(false);
  const [equipamentoSearch, setEquipamentoSearch] = useState('');

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      unit: '',
      areaGroup: '',
      areaSubgroup: '',
      area: '',
      equipment: '',
      serviceType: '',
      foundSituation: '',
      priority: '',
      sector: '',
      details: '',
      images: [],
      acceptTerms: false,
      whatsappContact: false,
      receiveEmail: false,
    },
    shouldUnregister: false,
  });

  const selectedUnit = watch('unit');
  const selectedAreaGroup = watch('areaGroup');
  const selectedAreaSubgroup = watch('areaSubgroup');
  const selectedArea = watch('area');
  const selectedServiceType = watch('serviceType');
  const selectedFoundSituation = watch('foundSituation');

  const isVisible = useCallback((id: FieldId) => fieldConfig[id]?.visible, [fieldConfig]);
  const isRequired = useCallback((id: FieldId) => fieldConfig[id]?.required, [fieldConfig]);
  const isLocked = useCallback((id: FieldId) => fieldConfig[id]?.locked, [fieldConfig]);
  const labelOf = useCallback((id: FieldId) => fieldConfig[id]?.label ?? FIELD_META[id].defaultLabel, [fieldConfig]);

  const unitLabel = useMemo(() => chamadoWobaCookie?.nome || String(unitId || ''), [chamadoWobaCookie?.nome, unitId]);
  const areaLabel = useMemo(() => chamadoWobaCookie?.ambiente?.label || '', [chamadoWobaCookie?.ambiente?.label]);

  // Rótulo a exibir num campo travado (preenchido pelo QR) — evita mostrar o ID cru.
  const lockedLabelOf = useCallback(
    (id: FieldId): string => {
      switch (id) {
        case 'unit':
          return unitLabel;
        case 'area':
          return areaLabel;
        case 'areaGroup':
          return chamadoWobaCookie?.labels?.areaGroup || '';
        case 'areaSubgroup':
          return chamadoWobaCookie?.labels?.areaSubgroup || '';
        case 'equipment':
          return chamadoWobaCookie?.labels?.equipment || '';
        default:
          return '';
      }
    },
    [unitLabel, areaLabel, chamadoWobaCookie?.labels],
  );

  // ── Inicialização ──────────────────────────────────────────────────────────
  // Fluxo 100% sem login: usa apenas endpoints públicos (sufixo /public). Não há
  // mais auto-login com conta de serviço — a config da própria empresa libera o
  // formulário no backend.

  useEffect(() => {
    if (!chamadoWobaCookie?.qrcode) {
      push(`/qr/${qrcode}`);
      return;
    }
    if (chamadoWobaCookie.qrcode != qrcode) {
      push(`/qr/${qrcode}`);
      return;
    }

    const init = async () => {
      setIsLoading(true);
      try {
        const formConfigRes = await ChamadoService.getPublicFormConfig({ empresaId });

        const apiConfig = formConfigRes.data as ApiConfig;
        const builtConfig = buildFieldConfig(apiConfig);
        const labels = buildPageLabels(apiConfig);

        // Flag de notificação vem na leitura pública do QR (gravada no cookie pelo LandingQR).
        const notifyByEmail = chamadoWobaCookie.notifyByEmail === true;
        setNotifyRequestersByEmail(notifyByEmail);

        // Se notificações habilitadas, nome e email são sempre visíveis e obrigatórios
        if (notifyByEmail) {
          builtConfig.name = { ...builtConfig.name, visible: true, required: true };
          builtConfig.email = { ...builtConfig.email, visible: true, required: true };
        }

        // Aplicar locks conforme tipo do QR code
        builtConfig.unit = { ...builtConfig.unit, visible: true, required: true, locked: true };
        setValue('unit', String(unitId || ''));

        if (qrCodeType === 'A' || qrCodeType === 'E') {
          const { grupoArea, subGrupoArea, area } = chamadoWobaCookie.newChamado || {};
          const ambienteValue = chamadoWobaCookie.ambiente?.value;

          if (grupoArea) {
            builtConfig.areaGroup = { ...builtConfig.areaGroup, locked: true };
            setValue('areaGroup', String(grupoArea));
          }
          if (subGrupoArea) {
            builtConfig.areaSubgroup = { ...builtConfig.areaSubgroup, locked: true };
            setValue('areaSubgroup', String(subGrupoArea));
          }
          const areaVal = area || ambienteValue;
          if (areaVal) {
            builtConfig.area = { ...builtConfig.area, locked: true };
            setValue('area', String(areaVal));
          }
        }

        if (qrCodeType === 'E') {
          const equipVal = (chamadoWobaCookie as any)?.equipamento?.valueNumber;
          if (equipVal) {
            builtConfig.equipment = { ...builtConfig.equipment, locked: true };
            setValue('equipment', String(equipVal));
          }
        }

        setFieldConfig(builtConfig);
        setPageLabels(labels);

        // Seção inicial: pular "contact" se nenhum campo de contato visível
        const hasContactFields = builtConfig.name.visible || builtConfig.phone.visible || builtConfig.email.visible;
        setRevealedSections(hasContactFields ? ['contact'] : ['location']);
      } catch (error) {
        removeCookie('chamadoRoute', { path: '/', domain: URLS.cookieDomain });
        console.error(error);
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Opções dos selects ─────────────────────────────────────────────────────

  const populateOptions = useCallback(async () => {
    if (!empresaId || !selectedUnit) return;

    const grupoAreaId = getValues('areaGroup') || null;
    const subGrupoAreaId = getValues('areaSubgroup') || null;
    const areaId = getValues('area') || null;
    const system = getValues('serviceType') || null;

    const maybeFetch = <T>(condition: unknown, fn: () => Promise<T>) => (condition ? fn() : Promise.resolve(undefined));

    try {
      // allSettled: a falha de um filtro (ex.: 400) não pode zerar todos os selects.
      const settled = await Promise.allSettled([
        maybeFetch(isVisible('serviceType'), () =>
          FiltersService.getSystemsPublic({ companyId: empresaId, unitIds: selectedUnit }),
        ),
        maybeFetch(isVisible('areaGroup') && !isLocked('areaGroup'), () =>
          ChamadoService.getGrupoAreasPublic({ empresa: empresaId, siteId: selectedUnit }),
        ),
        maybeFetch(isVisible('areaSubgroup') && !isLocked('areaSubgroup'), () =>
          ChamadoService.getSubGrupoAreasPublic({ empresa: empresaId, siteId: selectedUnit, grupoAreaId }),
        ),
        maybeFetch(isVisible('area') && !isLocked('area'), () =>
          ChamadoService.getAreasPublic({
            empresa: empresaId,
            siteId: selectedUnit,
            grupoAreaId,
            subGrupoAreaId,
            PageSize: 50,
            PageIndex: 1,
            Search: areaSearch,
          }),
        ),
        maybeFetch(isVisible('equipment') && !isLocked('equipment'), () =>
          ChamadoService.getEquipmentsPublic({
            empresa: empresaId,
            SiteId: selectedUnit,
            AreaId: areaId,
            GrupoAreaId: grupoAreaId,
            SubGrupoAreaId: subGrupoAreaId,
            PageIndex: 1,
            PageSize: 50,
            Search: equipamentoSearch,
          }),
        ),
        maybeFetch(isVisible('foundSituation'), () =>
          ChamadoService.getOcorrenciasTipicasPublic({
            EmpresaId: empresaId,
            UnidadeId: selectedUnit,
            system,
            grupoAreadId: grupoAreaId,
            subGrupoAreadId: subGrupoAreaId,
            tipoEquipamentoId: null,
          }),
        ),
        maybeFetch(isVisible('priority'), () =>
          ChamadoService.getPrioritiesPublic({ unidadeId: selectedUnit, empresaId }),
        ),
        maybeFetch(isVisible('sector'), () => ChamadoService.getSetoresPublic({ unidadeId: selectedUnit, empresaId })),
      ]);

      const [systems, areaGroups, areaSubgroups, areas, equipments, typical, priorities, setores] = settled.map(r =>
        r.status === 'fulfilled' ? r.value : undefined,
      );

      areaPageRef.current = 1;
      equipamentoPageRef.current = 1;

      setOptions(prev => ({
        ...prev,
        systems: systems?.data?.map(({ nome, sistemaEmpresa }: any) => ({ label: nome, value: sistemaEmpresa })),
        areaGroups: areaGroups?.data?.map(({ grupoArea, agrupamento }: any) => ({
          label: agrupamento,
          value: grupoArea,
        })),
        areaSubgroups: areaSubgroups?.data?.map(({ nome, subGrupoArea }: any) => ({
          label: nome,
          value: subGrupoArea,
        })),
        areas: areas?.data?.map(({ area, nome, identificacaoAmbiente, ...rest }: any) => ({
          label: identificacaoAmbiente ? `${nome}/${identificacaoAmbiente}` : nome,
          value: area,
          ...rest,
        })),
        equipments: equipments?.data?.map(({ equipamento, nome, tag, ...rest }: any) => ({
          label: tag ? `${nome}/${tag}` : nome,
          value: equipamento,
          ...rest,
        })),
        typical: typical?.data?.map(({ descricao, anomaliaId, ...rest }: any) => ({
          label: descricao,
          value: anomaliaId,
          ...rest,
        })),
        priorities: priorities?.data?.map(({ descricao, prioridadeAnomalia }: any) => ({
          label: descricao,
          value: prioridadeAnomalia,
        })),
        setores: setores?.data?.map(({ nome, setor }: any) => ({ label: nome, value: setor })),
      }));
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    empresaId,
    selectedUnit,
    selectedAreaGroup,
    selectedAreaSubgroup,
    selectedArea,
    selectedServiceType,
    isVisible,
    isLocked,
  ]);

  useEffect(() => {
    const timer = setTimeout(populateOptions, 200);
    return () => clearTimeout(timer);
  }, [populateOptions]);

  // ── Auto-preenchimento da típica ───────────────────────────────────────────

  const setFieldIfValue = useCallback(
    (field: keyof FormValues, value: unknown) => {
      if (value !== undefined && value !== null && value !== '') {
        setValue(field, value as any);
      }
    },
    [setValue],
  );

  useEffect(() => {
    if (!selectedFoundSituation || !options?.typical) return;
    const selectedTypical = options.typical.find(({ value }) => value === selectedFoundSituation);
    if (!selectedTypical) return;

    setFieldIfValue('details', selectedTypical.label);
    setFieldIfValue('serviceType', selectedTypical.sistemaEmpresaId ?? selectedTypical.sistemaEmpresa);
    setFieldIfValue('priority', selectedTypical.prioridadeAnomaliaId ?? selectedTypical.prioridadeAnomalia);
    setFieldIfValue('sector', selectedTypical.setorId ?? selectedTypical.setor);

    if (!isLocked('areaGroup')) setFieldIfValue('areaGroup', selectedTypical.grupoAreaId ?? selectedTypical.grupoArea);
    if (!isLocked('areaSubgroup'))
      setFieldIfValue('areaSubgroup', selectedTypical.subgrupoAreaId ?? selectedTypical.subGrupoArea);
    if (!isLocked('area')) setFieldIfValue('area', selectedTypical.areaId);
    if (!isLocked('equipment')) setFieldIfValue('equipment', selectedTypical.equipamentoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFoundSituation, options?.typical]);

  // ── Busca remota / paginação infinita ─────────────────────────────────────

  const loadMoreAreas = useCallback(async () => {
    if (loadingMoreAreasRef.current || !empresaId) return;
    loadingMoreAreasRef.current = true;
    const nextPage = areaPageRef.current + 1;
    areaPageRef.current = nextPage;
    try {
      const res = await ChamadoService.getAreasPublic({
        empresa: empresaId,
        siteId: getValues('unit'),
        grupoAreaId: getValues('areaGroup') || null,
        subGrupoAreaId: getValues('areaSubgroup') || null,
        PageSize: 50,
        PageIndex: nextPage,
        Search: areaSearch,
      });
      const sanitized = res?.data?.map(({ area, nome, identificacaoAmbiente, ...rest }: any) => ({
        label: identificacaoAmbiente ? `${nome}/${identificacaoAmbiente}` : nome,
        value: area,
        ...rest,
      }));
      setOptions(prev => ({ ...prev, areas: [...(prev?.areas || []), ...(sanitized || [])] }));
    } catch (error) {
      areaPageRef.current = nextPage - 1;
      console.error(error);
    } finally {
      loadingMoreAreasRef.current = false;
    }
  }, [areaSearch, empresaId, getValues]);

  const debounceAreaRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchArea = useCallback(
    (term: string) => {
      setAreaSearch(term);
      areaPageRef.current = 1;
      if (debounceAreaRef.current) clearTimeout(debounceAreaRef.current);
      debounceAreaRef.current = setTimeout(async () => {
        if (!empresaId) return;
        try {
          const res = await ChamadoService.getAreasPublic({
            empresa: empresaId,
            siteId: getValues('unit'),
            grupoAreaId: getValues('areaGroup') || null,
            subGrupoAreaId: getValues('areaSubgroup') || null,
            PageSize: 50,
            PageIndex: 1,
            Search: term,
          });
          const sanitized = res?.data?.map(({ area, nome, identificacaoAmbiente, ...rest }: any) => ({
            label: identificacaoAmbiente ? `${nome}/${identificacaoAmbiente}` : nome,
            value: area,
            ...rest,
          }));
          setOptions(prev => ({ ...prev, areas: sanitized }));
        } catch (error) {
          console.error(error);
        }
      }, 400);
    },
    [empresaId, getValues],
  );

  const loadMoreEquipamentos = useCallback(async () => {
    if (loadingMoreEquipamentosRef.current || !empresaId) return;
    loadingMoreEquipamentosRef.current = true;
    const nextPage = equipamentoPageRef.current + 1;
    equipamentoPageRef.current = nextPage;
    try {
      const res = await ChamadoService.getEquipmentsPublic({
        empresa: empresaId,
        SiteId: getValues('unit'),
        AreaId: getValues('area') || null,
        GrupoAreaId: getValues('areaGroup') || null,
        SubGrupoAreaId: getValues('areaSubgroup') || null,
        PageSize: 50,
        PageIndex: nextPage,
        Search: equipamentoSearch,
      });
      const sanitized = res?.data?.map(({ equipamento, nome, tag, ...rest }: any) => ({
        label: tag ? `${nome}/${tag}` : nome,
        value: equipamento,
        ...rest,
      }));
      setOptions(prev => ({ ...prev, equipments: [...(prev?.equipments || []), ...(sanitized || [])] }));
    } catch (error) {
      equipamentoPageRef.current = nextPage - 1;
      console.error(error);
    } finally {
      loadingMoreEquipamentosRef.current = false;
    }
  }, [empresaId, equipamentoSearch, getValues]);

  const debounceEquipamentoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchEquipamento = useCallback(
    (term: string) => {
      setEquipamentoSearch(term);
      equipamentoPageRef.current = 1;
      if (debounceEquipamentoRef.current) clearTimeout(debounceEquipamentoRef.current);
      debounceEquipamentoRef.current = setTimeout(async () => {
        if (!empresaId) return;
        try {
          const res = await ChamadoService.getEquipmentsPublic({
            empresa: empresaId,
            SiteId: getValues('unit'),
            AreaId: getValues('area') || null,
            GrupoAreaId: getValues('areaGroup') || null,
            SubGrupoAreaId: getValues('areaSubgroup') || null,
            PageSize: 50,
            PageIndex: 1,
            Search: term,
          });
          const sanitized = res?.data?.map(({ equipamento, nome, tag, ...rest }: any) => ({
            label: tag ? `${nome}/${tag}` : nome,
            value: equipamento,
            ...rest,
          }));
          setOptions(prev => ({ ...prev, equipments: sanitized }));
        } catch (error) {
          console.error(error);
        }
      }, 400);
    },
    [empresaId, getValues],
  );

  // ── Limpeza em cascata ─────────────────────────────────────────────────────

  const clearDependents = useCallback(
    (fieldId: 'unit' | 'areaGroup' | 'areaSubgroup' | 'area' | 'serviceType') => {
      const cascade: Partial<Record<typeof fieldId, Array<keyof FormValues>>> = {
        unit: ['areaGroup', 'areaSubgroup', 'area', 'equipment', 'serviceType', 'foundSituation', 'priority', 'sector'],
        areaGroup: ['areaSubgroup', 'area', 'equipment'],
        areaSubgroup: ['area', 'equipment'],
        area: ['equipment'],
        serviceType: ['foundSituation'],
      };
      (cascade[fieldId] || []).forEach(dep => {
        if (!isLocked(dep as FieldId)) {
          setValue(dep, '');
        }
      });
      setAreaSearch('');
      setEquipamentoSearch('');
    },
    [isLocked, setValue],
  );

  // ── Revelação progressiva de seções ───────────────────────────────────────

  const SECTION_FIELDS: Record<SectionKey, Array<keyof FormValues>> = useMemo(
    () => ({
      contact: (['name', 'phone', 'email', 'acceptTerms'] as Array<keyof FormValues>).filter(f => {
        if (f === 'acceptTerms') return fieldConfig.name.visible || fieldConfig.email.visible;
        return fieldConfig[f as FieldId]?.visible && !fieldConfig[f as FieldId]?.locked;
      }),
      location: (['unit', 'areaGroup', 'areaSubgroup', 'area', 'equipment'] as Array<keyof FormValues>).filter(
        f => fieldConfig[f as FieldId]?.visible && !fieldConfig[f as FieldId]?.locked,
      ),
      situation: (
        ['serviceType', 'foundSituation', 'priority', 'sector', 'details', 'images'] as Array<keyof FormValues>
      ).filter(f => {
        if (f === 'images') return fieldConfig.photos.visible && !fieldConfig.photos.locked;
        return fieldConfig[f as FieldId]?.visible && !fieldConfig[f as FieldId]?.locked;
      }),
    }),
    [fieldConfig],
  );

  const sectionOrder: SectionKey[] = useMemo(() => {
    const hasContact = fieldConfig.name.visible || fieldConfig.phone.visible || fieldConfig.email.visible;
    return hasContact ? ['contact', 'location', 'situation'] : ['location', 'situation'];
  }, [fieldConfig]);

  const continueSection = useCallback(
    async (current: SectionKey) => {
      const fieldsToValidate = SECTION_FIELDS[current];
      const valid = fieldsToValidate.length === 0 || (await trigger(fieldsToValidate));
      if (!valid) return;

      const idx = sectionOrder.indexOf(current);
      const next = sectionOrder[idx + 1];
      if (next) setRevealedSections(prev => [...prev, next]);
    },
    [SECTION_FIELDS, sectionOrder, trigger],
  );

  // ── Submit ─────────────────────────────────────────────────────────────────

  const onSubmit = useCallback(
    async (data: FormValues) => {
      if (!empresaId) return;
      setIsSubmitting(true);
      try {
        const now = new Date();

        const selectedTypical = options?.typical?.find(({ value }) => value === data.foundSituation);
        const selectedEquipment = options?.equipments?.find(({ value }) => value === data.equipment);

        // Dados do solicitante: name/email/phone são novos campos sem login
        // Concatenamos na descrição enquanto o backend não tem campos dedicados
        const contactInfo = [data.name, data.email, data.phone].filter(Boolean).join(' | ');
        const descricao = contactInfo ? `${contactInfo} — ${data.details}` : data.details;

        const fotoEntries = (data.images || []).map((file, i) => ({ file, fotoPath: `foto-${i}` }));

        const ocurrenceData = {
          empresaId,
          siteId: data.unit || unitId,
          tipoAnomalia: 19,
          nomeSolicitante: data.name || null,
          emailSolicitante: data.email || null,
          telefoneSolicitante: data.phone || null,
          descricao,
          dataRegistro: new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString(),
          anomaliaTipicaId: data.foundSituation || null,
          grupoAreaId: data.areaGroup || null,
          subgrupoAreaId: data.areaSubgroup || null,
          areaId: data.area || chamadoWobaCookie?.ambiente?.value || null,
          tipoEquipamentoId: selectedEquipment?.equipmentTypeId ?? selectedEquipment?.tipoEquipamentoId ?? null,
          equipamentoId: data.equipment || null,
          sistemaEmpresaId: data.serviceType || null,
          prioridadeAnomaliaId: data.priority || selectedTypical?.prioridadeAnomaliaId || null,
          setorId: data.sector || selectedTypical?.setorId || null,
          // Backend público exige o aceite de termos. Quando há coleta de dados pessoais
          // (Nome/E-mail), envia o aceite real do checkbox; caso contrário envia true,
          // pois não há consentimento a coletar e o campo é obrigatório.
          aceiteTermos: isVisible('name') || isVisible('email') ? !!data.acceptTerms : true,
          // Sem login: não há emitente autenticado; o solicitante vai em nome/email/telefone.
          emitentes: [],
          executores:
            (selectedTypical?.executores as Array<{ executor: string | number }> | undefined)?.map(e => ({
              executorId: e.executor,
            })) || [],
          fotos: fotoEntries.map(({ fotoPath }) => ({ fotoPath, anomaliaFotoGid: null })),
          temFoto: fotoEntries.length > 0,
          plataforma: 1,
          indisponibilidade: false,
          permitirContato: data.whatsappContact,
        };

        const chamadoResponse = await ChamadoService.saveOccurencesPublic({
          empresaId,
          unidadeId: data.unit || unitId,
          data: ocurrenceData,
          validarDuplicidade: false,
        });

        if (chamadoResponse.data.success) {
          if (fotoEntries.length > 0) {
            // A API devolve uploadToken como objeto { token, expiresAt } (a doc dizia string).
            // Extrai o JWT de forma defensiva para os dois formatos — senão vai como
            // "Bearer [object Object]" no header e o Media Center rejeita o upload.
            const rawUploadToken = chamadoResponse.data.uploadToken;
            const uploadToken = typeof rawUploadToken === 'string' ? rawUploadToken : rawUploadToken?.token;
            const fotoCreatedPaths = chamadoResponse.data.fotosResult?.fotoCreatedPaths || {};

            if (uploadToken) {
              const uploads = await Promise.allSettled(
                fotoEntries.map(({ file, fotoPath }) =>
                  ChamadoService.uploadPhotoPublic({
                    file,
                    filePath: fotoCreatedPaths[fotoPath],
                    token: uploadToken,
                  }),
                ),
              );
              uploads.forEach((result, i) => {
                if (result.status === 'rejected') {
                  Sentry.captureException(result.reason, { extra: { fotoPath: fotoEntries[i].fotoPath } });
                }
              });
            } else {
              Sentry.captureMessage('Chamado sem login: uploadToken ausente, fotos não anexadas', 'warning');
            }
          }

          setIsSuccess(true);
        }
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [empresaId, unitId, options, chamadoWobaCookie, isVisible],
  );

  const handleSubmit = useCallback(() => {
    rhfHandleSubmit(onSubmit)();
  }, [rhfHandleSubmit, onSubmit]);

  return {
    revealedSections,
    isSuccess,
    fieldConfig,
    options,
    pageLabels,
    isLoading,
    isSubmitting,
    notifyRequestersByEmail,
    qrCodeType,
    unitLabel,
    areaLabel,
    lockedLabelOf,
    continueSection,
    handleSubmit,
    control,
    errors,
    watch,
    setValue,
    getValues,
    isVisible,
    isRequired,
    isLocked,
    labelOf,
    clearDependents,
    searchArea,
    loadMoreAreas,
    searchEquipamento,
    loadMoreEquipamentos,
  };
}
